import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Alert, Input } from '../ui';
import { reportBug } from '../api/bugReports';
import { useTranslation } from '../context/TranslationContext';
import './BugReportModal.css';

const BugReportModal = ({ show, onClose, exceptionDetails, isAuthenticated }) => {
  const [description, setDescription] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [browserInfo, setBrowserInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const descRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    const info = `User Agent: ${navigator.userAgent}
URL: ${window.location.href}
Platform: ${navigator.platform}
Language: ${navigator.language}`;
    setBrowserInfo(info);

    if (show && descRef.current) {
      setTimeout(() => descRef.current.focus(), 200);
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await reportBug({ description, exceptionDetails, contactInfo, browserInfo });
      setIsSubmitted(true);
    } catch (err) {
      setError(t('bugReportError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDescription('');
    setContactInfo('');
    setError(null);
    setIsSubmitted(false);
    onClose();
  };

  return (
    <Modal
      open={show}
      onClose={handleClose}
      title={t('bugReportTitle')}
      size="md"
      footer={isSubmitted ? (
        <Button variant="primary" onClick={handleClose}>
          {t('bugReportClose')}
        </Button>
      ) : null}
    >
      {isSubmitted ? (
        <Alert variant="success">
          <div style={{ fontSize: '1.1em', marginBottom: 8 }}>{t('bugReportSuccessTitle')}</div>
          <div style={{ fontSize: '0.95em', color: '#388e3c' }}>
            {t('bugReportSuccessBody')}
          </div>
        </Alert>
      ) : (
        <form onSubmit={handleSubmit} autoComplete="off">
          <div style={{ marginBottom: 'var(--space-3, 0.75rem)' }}>
            <Input
              label={
                <span>
                  {t('bugReportDescription')}
                  <span className="required-asterisk" aria-label="required">*</span>
                </span>
              }
              as="textarea"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('bugReportDescriptionPlaceholder')}
              required
              ref={descRef}
              aria-required="true"
              aria-label={t('bugReportDescription')}
            />
          </div>
          <div style={{ marginBottom: 'var(--space-3, 0.75rem)' }}>
            <Input
              label={
                <span>
                  {t('bugReportContactInfo')}
                  <span style={{ color: '#888', fontWeight: 400 }}> (optional)</span>
                </span>
              }
              type="text"
              value={contactInfo}
              onChange={(e) => setContactInfo(e.target.value)}
              placeholder={t('bugReportContactInfoPlaceholder')}
              aria-label={t('bugReportContactInfo')}
            />
          </div>
          {exceptionDetails && (
            <div style={{ marginBottom: 'var(--space-3, 0.75rem)' }}>
              <Input
                label={
                  <span>
                    {t('bugReportExceptionDetails')}{' '}
                    <span style={{ color: '#888', fontWeight: 400 }}>{t('bugReportExceptionDetailsDev')}</span>
                  </span>
                }
                as="textarea"
                rows={5}
                readOnly
                defaultValue={exceptionDetails}
                aria-label={t('bugReportExceptionDetails')}
              />
            </div>
          )}
          {error && <Alert variant="danger">{error}</Alert>}
          <div style={{ marginBottom: 'var(--space-3, 0.75rem)' }}>
            <Input
              label={
                <span>
                  {t('bugReportBrowserInfo')}{' '}
                  <span style={{ color: '#888', fontWeight: 400 }}>{t('bugReportBrowserInfoAuto')}</span>
                </span>
              }
              as="textarea"
              rows={3}
              value={browserInfo}
              readOnly
              aria-label={t('bugReportBrowserInfo')}
            />
          </div>
          {!isAuthenticated && (
            <Alert variant="warning" style={{ marginBottom: 'var(--space-3, 0.75rem)' }}>
              {t('bugReportLoginRequired')}
            </Alert>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
              type="button"
              style={{ marginRight: 'var(--space-2, 0.5rem)' }}
            >
              {t('bugReportCancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || !isAuthenticated}
            >
              {isSubmitting ? t('bugReportSubmitting') : t('bugReportSubmit')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default BugReportModal;
